import isEqual from 'lodash/isEqual';

import { TFunction } from '@lyearn/i18n';
import {
  DifficultyEnum,
  MtfComponentInput,
  OptionInput,
  QuestionApplicableOnEnum,
  QuestionInput,
  QuestionSubTypeEnum,
  QuestionTextTypeEnum,
  QuestionTypeEnum,
  UpdateQuestionEntity,
} from '@/types/schema';

const getMtfPairings = (count: number) => {
  return Array(count)
    .fill(0)
    .map((_, index) => ({ leftElementIndex: index, rightElementIndices: [index] }));
};

export const getInitialDefaultQuestionValues = (
  currentSubWorkspace: string | null,
): Partial<QuestionInput> => ({
  questionText: '',
  questionTextType: QuestionTextTypeEnum.Html,
  correctAnswerFeedback: '',
  incorrectAnswerFeedback: '',
  difficulty: DifficultyEnum.Na,
  settings: {
    shuffleOptions: false,
  },
  subWorkspaceIds: currentSubWorkspace ? { set: [currentSubWorkspace] } : undefined,
});

export const getInitialQuestionState = (
  questionType: QuestionTypeEnum,
  t: TFunction,
  applicableOn: QuestionApplicableOnEnum,
  currentSubWorkspace: string | null,
): QuestionInput | null => {
  if (applicableOn !== QuestionApplicableOnEnum.Quiz) {
    return {
      ...getInitialDefaultQuestionValues(currentSubWorkspace),
      questionType,
      questionTextType: QuestionTextTypeEnum.Html,
      questionText: '',
      applicableOn: [applicableOn],
      subType:
        questionType === QuestionTypeEnum.Likert ? QuestionSubTypeEnum.LikertRating : undefined,
    };
  }
  switch (questionType) {
    case QuestionTypeEnum.SingleSelect:
    case QuestionTypeEnum.MultiSelect: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        options: Array(4).fill({
          optionText: '',
          optionEvaluation: false,
          optionRelativeScore: 100,
        }),
        subType: QuestionSubTypeEnum.SingleSelect,
      };
    }
    case QuestionTypeEnum.TrueOrFalse: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        options: [
          {
            optionText: t('QuizCreator:True'),
            optionEvaluation: false,
            optionRelativeScore: 100,
          },
          {
            optionText: t('QuizCreator:False'),
            optionEvaluation: false,
            optionRelativeScore: 100,
          },
        ],
        subType: QuestionSubTypeEnum.SingleSelect,
      };
    }
    case QuestionTypeEnum.MatchTheFollowing: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        mtfComponent: {
          leftElements: Array(4).fill({
            text: '',
          }),
          rightElements: Array(4).fill({
            text: '',
          }),
          pairings: getMtfPairings(4),
        },
        globalDistractors: [],
      };
    }
    case QuestionTypeEnum.FillInTheBlanks: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        globalDistractors: [],
        subWorkspaceIds: currentSubWorkspace ? { set: [currentSubWorkspace] } : undefined,
      };
    }
    case QuestionTypeEnum.PictureMultipleChoiceQuestion: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        options: Array(4).fill({
          optionText: '',
          optionEvaluation: false,
          optionRelativeScore: 100,
        }),
        subType: QuestionSubTypeEnum.SingleSelect,
      };
    }
    case QuestionTypeEnum.ImageHotspot: {
      return {
        ...getInitialDefaultQuestionValues(currentSubWorkspace),
        questionType,
        globalDistractors: [],
      };
    }
    default:
      return null;
  }
};

export const removeEmptyElements = (questions: UpdateQuestionEntity[]) => {
  const updatedQuestions = questions.map((question) => {
    if (question.questionType === QuestionTypeEnum.MatchTheFollowing) {
      const mtfComponent = question.mtfComponent;
      if (!mtfComponent) {
        return question;
      }

      const updatedMtfComponent: MtfComponentInput = {
        leftElements: [],
        pairings: [],
        rightElements: [],
      };
      Array(mtfComponent.leftElements.length)
        .fill(0)
        .forEach((_, index) => {
          const leftElement = mtfComponent.leftElements[index];
          const rightElement = mtfComponent.rightElements[index];
          if (leftElement.text && rightElement.text) {
            updatedMtfComponent.leftElements.push(leftElement);
            updatedMtfComponent.rightElements.push(rightElement);
          }
        });

      updatedMtfComponent.pairings.push(...getMtfPairings(updatedMtfComponent.leftElements.length));

      return { ...question, mtfComponent: updatedMtfComponent };
    } else if (question.questionType === QuestionTypeEnum.FillInTheBlanks) {
      const ftbComponents = question.ftbComponents?.set || [];
      const filteredFtbComponents = ftbComponents.filter(
        (component) => !component.isBlank || (component.isBlank && !!component.text),
      );

      if (ftbComponents.length === filteredFtbComponents.length) {
        return question;
      }

      return { ...question, ftbComponents: { set: filteredFtbComponents } };
    } else if (question.questionType === QuestionTypeEnum.PictureMultipleChoiceQuestion) {
      const options = question.options?.set || [];
      const filteredOptions = options.filter((o) => !!o.optionText || !!o.attachments?.length);

      if (options.length === filteredOptions.length) {
        return question;
      }

      return { ...question, options: { set: filteredOptions } };
    } else {
      const options = question.options?.set || [];
      const filteredOptions = options.filter((option) => !!option.optionText);

      if (options.length === filteredOptions.length) {
        return question;
      }

      return { ...question, options: { set: filteredOptions } };
    }
  });

  return updatedQuestions;
};

export const getIsCorrectAnswerChanged = (
  options?: OptionInput[],
  updatedOptions?: OptionInput[],
) => {
  return !isEqual(
    options?.map(({ optionId, optionEvaluation }) => ({
      optionId,
      optionEvaluation,
    })),
    updatedOptions?.map(({ optionId, optionEvaluation }) => ({
      optionId,
      optionEvaluation,
    })),
  );
};
